import { isScriptAllowed } from '@primer-io/sdk-utils/pciCompliance';
import { VerificationError } from './VerificationError';

/**
 * Load a classic JavaScript file with SRI integrity checking.
 *
 * Unlike `importModuleWithSRI`, this does NOT use `import()` — the script is
 * loaded via a classic `<script>` tag (no `type="module"`) and is expected
 * to expose its API via a global (typically a UMD bundle's auto-set
 * `globalThis.<Name>` assignment).
 *
 * Trade-off vs. ESM dynamic import:
 *  + No `import()` expression in the SDK source — bundlers (webpack/Rspack,
 *    Vite, esbuild, Next.js, etc.) cannot rewrite, context-module-scan, or
 *    statically analyze this load. The CSP only needs `script-src` for the
 *    target host; no `'unsafe-eval'` is involved at any layer.
 *  + Same SRI guarantee — the browser verifies `integrity` identically for
 *    classic and module scripts.
 *  - Loaded code must expose its API via a known global. The caller is
 *    responsible for reading from `window.<global>` after this resolves.
 *
 * The script tag is removed from the DOM after `onload`/`onerror` to avoid
 * leaving inert nodes in `<head>`. Removing the tag does not unset the
 * global the script created.
 */
export async function loadScriptWithSRI(
  url: string,
  sha256: string,
  { fetchPriority }: { fetchPriority?: 'high' | 'low' | 'auto' } = {},
): Promise<void> {
  if (!isScriptAllowed(url)) {
    throw new VerificationError(
      `Script source not authorized per PCI DSS 6.4.3: ${url}`,
    );
  }
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.integrity = `sha256-${sha256}`;
    script.crossOrigin = 'anonymous';
    if (fetchPriority) {
      script.setAttribute('fetchpriority', fetchPriority);
    }
    script.onload = () => {
      script.remove();
      resolve();
    };
    script.onerror = () => {
      script.remove();
      reject(new Error(`SRI check or load failed for script: ${url}`));
    };
    document.head.appendChild(script);
  });
}
